const GA_MEASUREMENT_ID = String(import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-YTCCPYX0YP').trim();
const GOOGLE_ADS_ID = String(import.meta.env.VITE_GOOGLE_ADS_ID || 'AW-863149782').trim();
const GOOGLE_ADS_BOOKING_LABEL = String(import.meta.env.VITE_GOOGLE_ADS_BOOKING_LABEL || '').trim();
const GOOGLE_ADS_LEAD_LABEL = String(import.meta.env.VITE_GOOGLE_ADS_LEAD_LABEL || '').trim();

function canTrack() {
  return typeof window !== 'undefined';
}

function hasGtag() {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/** Load gtag.js once and configure GA4. */
export function initGoogleAnalytics() {
  try {
    if (!canTrack() || window.__invincibleGaInitialized) return;
    window.__invincibleGaInitialized = true;

    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function gtag() {
        window.dataLayer.push(arguments);
      };

    window.gtag('js', new Date());

    if (GA_MEASUREMENT_ID) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        send_page_view: false
      });

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
      document.head.appendChild(script);
    }
  } catch (err) {
    // Non-blocking fail-safe
  }
}

/** SPA page view (React Router). */
export function trackPageView(path) {
  try {
    if (!hasGtag()) return;
    const pagePath = path || `${window.location.pathname}${window.location.search}`;
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title
    });
  } catch (err) {
    // Non-blocking fail-safe
  }
}

export function getGaMeasurementId() {
  return GA_MEASUREMENT_ID || null;
}

export function getGoogleAdsId() {
  return GOOGLE_ADS_ID || 'AW-863149782';
}

export function getGoogleAdsBookingLabel() {
  return GOOGLE_ADS_BOOKING_LABEL || null;
}

export function getGoogleAdsLeadLabel() {
  return GOOGLE_ADS_LEAD_LABEL || null;
}

/** Helper to format Indian phone numbers to E.164 for Google Enhanced Conversions */
function formatE164Phone(rawPhone) {
  if (!rawPhone) return '';
  const digits = String(rawPhone).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `+91${digits}`;
  if (digits.startsWith('91') && digits.length === 12) return `+${digits}`;
  return `+${digits}`;
}

/**
 * Safely send enhanced conversion user data to Google Ads / GA4.
 * Google automatically hashes and matches this with signed-in Google accounts.
 */
function sendUserDataSafely(customerData = {}) {
  try {
    if (!hasGtag()) return;
    const userData = {};

    if (customerData.email) {
      userData.email = String(customerData.email).trim().toLowerCase();
    }
    if (customerData.phone) {
      const e164 = formatE164Phone(customerData.phone);
      if (e164) userData.phone_number = e164;
    }
    if (customerData.firstName) {
      userData.address = {
        first_name: String(customerData.firstName).trim(),
        last_name: String(customerData.lastName || '').trim()
      };
    }

    if (Object.keys(userData).length > 0) {
      window.gtag('set', 'user_data', userData);
    }
  } catch (err) {
    // Non-blocking fail-safe
  }
}

/**
 * Track Booking / Purchase Conversion (Primary Google Ads Conversion).
 * Safe, idempotent (deduplicated by booking ID), completely non-blocking.
 */
export function trackBookingConversion(booking) {
  try {
    if (!booking) return;

    const bookingId = String(
      booking.bookingCode ||
      booking.booking_code ||
      booking.id ||
      ''
    ).trim().replace(/^#/, '');

    if (!bookingId) return;

    // Deduplication check via sessionStorage so page refreshes don't double count
    const dedupeKey = `inv_conv_tracked_${bookingId}`;
    try {
      if (window.sessionStorage && window.sessionStorage.getItem(dedupeKey)) {
        return;
      }
      if (window.sessionStorage) {
        window.sessionStorage.setItem(dedupeKey, '1');
      }
    } catch (_) {
      // Storage access blocked (e.g. strict private mode), continue safely
    }

    if (!hasGtag()) return;

    // Extract booking financials
    const amount = Number(
      booking.amountPaidOnline ??
      booking.advanceAmount ??
      booking.totalAmount ??
      booking.amount ??
      0
    );

    // Extract customer details for Enhanced Conversions
    const primaryTraveler = Array.isArray(booking.travelers) && booking.travelers[0] ? booking.travelers[0] : null;
    const customerEmail = primaryTraveler?.email || booking.user?.email || booking.customerEmail || '';
    const customerPhone = primaryTraveler?.phone || booking.primaryPhone || booking.user?.phone || booking.customerPhone || '';
    const firstName = primaryTraveler?.firstName || booking.user?.firstName || '';
    const lastName = primaryTraveler?.lastName || booking.user?.lastName || '';

    sendUserDataSafely({
      email: customerEmail,
      phone: customerPhone,
      firstName,
      lastName
    });

    const trekName = booking.batch?.trek?.name || booking.trekName || 'Trek Camp Booking';
    const trekId = String(booking.batch?.trekId || booking.batch?.trek?.id || booking.trekId || bookingId);
    const travelerCount = Number(booking.travelerCount || (booking.travelers ? booking.travelers.length : 1)) || 1;

    // 1. Google Ads Conversion Event
    const adsId = getGoogleAdsId();
    const bookingLabel = getGoogleAdsBookingLabel();
    const sendTo = bookingLabel ? `${adsId}/${bookingLabel}` : adsId;

    window.gtag('event', 'conversion', {
      send_to: sendTo,
      value: amount,
      currency: 'INR',
      transaction_id: bookingId
    });

    // 2. Standard E-commerce Purchase Event for GA4 & Google Ads Smart Bidding
    window.gtag('event', 'purchase', {
      transaction_id: bookingId,
      value: amount,
      currency: 'INR',
      items: [
        {
          item_id: trekId,
          item_name: trekName,
          price: amount,
          quantity: travelerCount
        }
      ]
    });
  } catch (err) {
    // Non-blocking fail-safe - guarantees checkout page will never break
  }
}

/**
 * Track Begin Checkout Event (User enters booking process).
 */
export function trackBeginCheckout({ batchId, trekName, price } = {}) {
  try {
    if (!hasGtag()) return;
    window.gtag('event', 'begin_checkout', {
      currency: 'INR',
      value: Number(price || 0),
      items: [
        {
          item_id: String(batchId || ''),
          item_name: String(trekName || 'Trek Camp'),
          price: Number(price || 0),
          quantity: 1
        }
      ]
    });
  } catch (err) {
    // Non-blocking fail-safe
  }
}

/**
 * Track Lead / Inquiry Form Submission.
 */
export function trackLeadSubmission({ formType, email, phone, fullName } = {}) {
  try {
    if (!hasGtag()) return;

    if (email || phone || fullName) {
      sendUserDataSafely({
        email,
        phone,
        firstName: fullName
      });
    }

    const adsId = getGoogleAdsId();
    const leadLabel = getGoogleAdsLeadLabel();

    // 1. Google Ads Conversion (if lead label configured)
    if (leadLabel) {
      window.gtag('event', 'conversion', {
        send_to: `${adsId}/${leadLabel}`
      });
    }

    // 2. Standard GA4 generate_lead event
    window.gtag('event', 'generate_lead', {
      event_category: 'Lead',
      event_label: formType || 'Inquiry',
      value: 1
    });
  } catch (err) {
    // Non-blocking fail-safe
  }
}

/**
 * Track PDF Brochure Download (High user interest).
 */
export function trackBrochureDownload(trekName) {
  try {
    if (!hasGtag()) return;
    window.gtag('event', 'download_brochure', {
      event_category: 'Brochure',
      event_label: String(trekName || 'Trek Brochure'),
      trek_name: String(trekName || '')
    });
  } catch (err) {
    // Non-blocking fail-safe
  }
}

/**
 * Track WhatsApp or Phone Call Click.
 */
export function trackContactClick({ type, destination } = {}) {
  try {
    if (!hasGtag()) return;
    window.gtag('event', 'contact_click', {
      contact_type: type || 'contact',
      destination: String(destination || ''),
      event_category: 'Contact',
      event_label: `${type}: ${destination}`
    });
  } catch (err) {
    // Non-blocking fail-safe
  }
}

/**
 * Track Trek Page View (For Dynamic Remarketing).
 */
export function trackViewItem({ id, name, category, price } = {}) {
  try {
    if (!hasGtag()) return;
    window.gtag('event', 'view_item', {
      currency: 'INR',
      value: Number(price || 0),
      items: [
        {
          item_id: String(id || ''),
          item_name: String(name || ''),
          item_category: String(category || 'Trek'),
          price: Number(price || 0),
          quantity: 1
        }
      ]
    });
  } catch (err) {
    // Non-blocking fail-safe
  }
}
